import React from 'react';
import { definePage, defineResource, TenantParams } from '@restavo/crud';
import EmptyState from 'src/components/Base/EmptyState';
import TableForm from 'src/components/Table/TableForm';
import { useCurrentRestaurant } from 'src/hooks/useRestaurants';
import {
  orgRestaurantBranches,
  tenantTableCreate,
  tenantTableDelete,
  tenantTableUpdate,
  tenantTables,
} from 'src/services/apis/tenantRequests';
import { QUERIES } from 'src/utils/react-query';

/**
 * Module-scope so the branch filter chip and the branch column share one options fetch
 * (spec §7.5). The generated branches wrapper takes no query params and needs none: the
 * server returns every branch, which is the scale the reference cache expects.
 */
const branchReference = (params: TenantParams) => orgRestaurantBranches(params);

const restaurantTables = defineResource({
  key: QUERIES.TABLES,
  // The hand-written hook sent no limit, so the server (alwaysPaginate, no configured take)
  // returned every row in one shot; the crud layer's paginated query needs a concrete page
  // size, and 1000 is the practical match — the same reach the pilot used for an
  // effectively-unbounded list.
  pageSize: 1000,
  api: {
    list: tenantTables,
    create: tenantTableCreate,
    update: tenantTableUpdate,
    remove: tenantTableDelete,
  },
  // The hand-written page filtered tableNumber in memory over the fetched set.
  search: 'client',
  searchFields: ['tableNumber'],
  filterable: [
    {
      field: 'restaurantBranchId',
      labelKey: 'common.branch',
      type: 'reference',
      load: branchReference,
      optionLabel: (branch: any) => branch.name,
    },
    { field: 'available', labelKey: 'common.available', type: 'boolean' },
  ],
});

export const restaurantTablesPage = definePage(restaurantTables, {
  // The hand-written page reused the branches page title ("{{restaurantName}}'s Branches"),
  // and a migration does not relabel the page it replaces.
  titleKey: 'titles.tableSettingsPage',
  descriptionKey: 'common.tables',
  createLabelKey: 'actions.createNewTable',
  updateLabelKey: 'actions.updateTable',
  deleteHeadingKey: 'messages.areYouSureToDeleteThisTable',
  columns: [
    // The column a user identifies a table by must survive every prefs reconciliation.
    { key: 'tableNumber', headerKey: 'common.tableNumber', sortable: true, alwaysVisible: true },
    { key: 'capacity', headerKey: 'common.capacity' },
    { key: 'summary', headerKey: 'common.summary' },
    {
      key: 'restaurantBranchId',
      headerKey: 'common.branch',
      display: {
        kind: 'reference',
        load: branchReference,
        optionLabel: (branch: any) => branch.name,
      },
    },
    // The hand-written cell was a dead per-row Toggle (no onToggle handler, so it never
    // changed anything) — the same yes/no copy as static text, like the pilot's menus page.
    { key: 'available', headerKey: 'common.available', display: { kind: 'boolean' } },
  ],
  form: TableForm,
  emptyState: <EmptyState />,
  toPayload: (values: any, params) => ({
    ...values,
    tableNumber: parseInt(values.tableNumber, 10),
    capacity: parseInt(values.capacity, 10),
    restaurantId: params.restaurantId,
  }),
  useTitleVars: () => ({ restaurantName: useCurrentRestaurant()?.name }),
});
